import { useEffect, useState } from 'react';

// types
import { AutocompleteWithTitles as AutocompleteWithTitlesProps } from './types';

// components
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import IconUp from '../icons/up';
import IconDown from '../icons/down';
import OptionList from './options-list';
import IconClose from '../icons/close';

// hooks
import useAutocomplete from '../autocomplete/hooks/use-autocomplete';
import useFilteredList from './use-filtered-list';

// styles
import styles from './autocomplete-with-titles.module.css';

export default function AutocompleteWithTitles({
  disabled = false,
  list = [],
  onChange,
  onSearch,
  placeholder = '',
  title = '',
  value = '',
  name = '',
  className = '',
  error = '',
  tabIndex,
  withClearButton,
  onClear,
}: AutocompleteWithTitlesProps): React.JSX.Element {
  const [search, setSearch] = useState('');

  const {
    isOptionsListVisible,
    setOptionsListVisible,
    searchQuery,
    handleSearch,
    onSelectChange,
    handleReset,
  } = useAutocomplete({
    value,
    onSearch,
    onChange,
    name,
    search,
    withTitles: true,
  });

  const filteredList = useFilteredList({ list, searchQuery });

  const onReset = () => {
    handleReset();

    if (onClear) {
      onClear();
    }
  };

  useEffect(() => {
    if (value) {
      list?.forEach((item) => {
        const currentItem = item.list.find(
          (listItem) => listItem.value === value,
        );

        if (currentItem) {
          setSearch(currentItem.name);
        }
      });
    }
  }, [value, searchQuery, list]);

  return (
    <ClickOutsideDetector setOptionsListVisible={setOptionsListVisible}>
      <>
        {title && <div className={styles.autocompleteTitle}>{title}</div>}
        <div
          className={`${styles.autocompleteWithTitles} ${
            disabled ? styles.autocompleteWithTitlesDisabled : ''
          } ${className}`}
        >
          <div className={styles.autocompleteInputWrapper}>
            <input
              type="text"
              name="search"
              className={`${styles.selectInputSearch} ${
                isOptionsListVisible ? styles.selectInputSearchActive : ''
              } ${error ? styles.selectInputSearchError : ''}`}
              onChange={handleSearch}
              value={searchQuery ?? ''}
              onFocus={() => setOptionsListVisible(true)}
              placeholder={placeholder}
              autoComplete="off"
              tabIndex={tabIndex}
              onKeyDown={(e) => e.key === 'Tab' && setOptionsListVisible(false)}
            />
            {isOptionsListVisible ? (
              <IconUp className={styles.inputIcon} />
            ) : (
              <IconDown className={styles.inputIcon} />
            )}
            {withClearButton && searchQuery && !disabled && (
              <button
                type="button"
                className={`${styles.iconWrapper} ${styles.iconWrapperClear}`}
                onClick={onReset}
                tabIndex={-1}
                aria-label="Close"
              >
                <IconClose className={styles.icon} />
              </button>
            )}
          </div>
          {error && <div className={styles.autocompleteError}>{error}</div>}
          {isOptionsListVisible && (
            <OptionList
              list={filteredList}
              value={value}
              onSelectChange={onSelectChange}
            />
          )}
        </div>
      </>
    </ClickOutsideDetector>
  );
}
